import { useEffect, useState } from "react";
import { Gauge } from "lucide-react";
import { computeScores } from "@/lib/scores";
import {
  PediatricNote, GcsCard, QsofaCard, SofaCard, ApacheCard, CardiacCard, WellsCard, CurbCard,
} from "@/components/scores/sections";

export default function ClinicalScores({ vitals = {}, age, pediatric = false, onScores }) {
  const [eye, setEye] = useState(4);
  const [verbal, setVerbal] = useState(5);
  const [motor, setMotor] = useState(6);
  const [apacheOpen, setApacheOpen] = useState(false);
  const [sofaOpen, setSofaOpen] = useState(false);
  const [cardiacOpen, setCardiacOpen] = useState(false);
  const [wellsOpen, setWellsOpen] = useState(false);
  const [curbOpen, setCurbOpen] = useState(false);
  const [ap, setAp] = useState({ pao2: "", fio2: "", ph: "", na: "", k: "", creat: "", arf: false, hct: "", wbc: "", chronic: 0 });
  const [sf, setSf] = useState({ pao2: "", fio2: "", vent: false, plt: "", bili: "", creat: "", cardio: 0 });
  const [ht, setHt] = useState({ history: 0, ecg: 0, risk: 0, trop: 0 });
  const [timi, setTimi] = useState({ age65: false, cad3: false, knownCad: false, asa: false, angina2: false, stDev: false, marker: false });
  const [wells, setWells] = useState({ signsDvt: false, peLikely: false, immob: false, prevVte: false, hemoptysis: false, malignancy: false });
  const [curb, setCurb] = useState({ bun: "" });

  const s = computeScores({
    vitals, age, eye, verbal, motor, ap, sf, ht, timi, wells, curb,
    pediatric, apacheOpen, sofaOpen, cardiacOpen, wellsOpen, curbOpen,
  });

  const scoresKey = JSON.stringify(s.payload);
  useEffect(() => {
    if (onScores) onScores(s.payload);
    // single dependency: re-emit only when the computed scores actually change.
    // scoresKey is the serialized payload — depending on s.payload (a new object
    // each render) instead would cause an infinite re-render loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoresKey]);

  const updAp = (k) => (e) => setAp((p) => ({ ...p, [k]: e.target.value }));
  const updSf = (k) => (e) => setSf((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div className="mt-8" data-testid="clinical-scores">
      <p className="text-sm font-semibold text-zinc-900 mb-3 flex items-center gap-2"><Gauge className="h-4 w-4 text-[#002FA7]" /> Critical-care scores</p>

      {pediatric && <PediatricNote />}

      <GcsCard eye={eye} verbal={verbal} motor={motor} setEye={setEye} setVerbal={setVerbal} setMotor={setMotor} gcsTotal={s.gcsTotal} gsev={s.gsev} />

      {!pediatric && (
        <>
          <QsofaCard qTotal={s.qTotal} qHigh={s.qHigh} />
          <SofaCard open={sofaOpen} onToggle={() => setSofaOpen((o) => !o)} sf={sf} updSf={updSf} setSf={setSf} sofaTotal={s.sofaTotal} />
          <ApacheCard open={apacheOpen} onToggle={() => setApacheOpen((o) => !o)} ap={ap} updAp={updAp} setAp={setAp} apacheTotal={s.apacheTotal} caption={s.apacheCaption} />
          <CardiacCard open={cardiacOpen} onToggle={() => setCardiacOpen((o) => !o)} ht={ht} setHt={setHt} timi={timi} setTimi={setTimi} heartTotal={s.heartTotal} hBand={s.hBand} timiTotal={s.timiTotal} heartAgePts={s.heartAgePts} />
          <WellsCard open={wellsOpen} onToggle={() => setWellsOpen((o) => !o)} wells={wells} setWells={setWells} hrOver100={s.hrOver100} wellsTotal={s.wellsTotal} wellsLikely={s.wellsLikely} />
          <CurbCard open={curbOpen} onToggle={() => setCurbOpen((o) => !o)} curb={curb} setCurb={setCurb} curbConfusion={s.curbConfusion} curbTotal={s.curbTotal} band={s.curbBandVal} />
        </>
      )}
    </div>
  );
}
